import { useCallback, useRef, useState } from "react";
import axios from "axios";

// =======================================================
// useAppointments
// -------------------------------------------------------
// Central hook for all appointment related API requests.
//
// Responsibilities:
// - Search appointments
// - Fetch calendar data
// - CRUD operations
// - Appointment actions
//
// This hook contains NO UI logic.
// It only communicates with the backend.
// =======================================================

const useAppointments = () => {
    const [appointments, setAppointments] = useState([]);
    const calendarAbortControllerRef = useRef(null);

    // =======================================================
    // SEARCH
    // =======================================================

    const fetchAppointments = useCallback(
        async (artistName, clientName) => {
            try {
                const response = await axios.get("/api/searchAppointments", {
                    params: {
                        artistName,
                        clientName,
                    },
                });

                setAppointments(response.data);
                return response.data;
            } catch (error) {
                console.error("Error fetching appointments:", error);
                return [];
            }
        },
        []
    );

    // =======================================================
    // CALENDAR
    // =======================================================

    const fetchAvailableDaysAndBookingStatus = useCallback(
        async (locationId) => {
            // Laufenden Kalender-Request sofort abbrechen,
            // wenn während des Ladens der Standort gewechselt wird.
            if (calendarAbortControllerRef.current) {
                calendarAbortControllerRef.current.abort();
            }

            const controller = new AbortController();
            calendarAbortControllerRef.current = controller;

            try {
                const [availableDaysResponse, bookingStatusResponse] =
                    await Promise.all([
                        axios.get(
                            `/api/available_days_with_artists?locationId=${locationId}`,
                            {
                                signal: controller.signal,
                            }
                        ),

                        axios.get(
                            `/api/booking-status?locationId=${locationId}`,
                            {
                                signal: controller.signal,
                            }
                        ),
                    ]);

                return {
                    availableDays: availableDaysResponse.data,
                    bookingStatus: bookingStatusResponse.data,
                };
            } catch (error) {
                if (
                    error?.code === "ERR_CANCELED" ||
                    error?.name === "CanceledError"
                ) {
                    return null;
                }

                throw error;
            } finally {
                if (
                    calendarAbortControllerRef.current === controller
                ) {
                    calendarAbortControllerRef.current = null;
                }
            }
        },
        []
    );

    // =======================================================
    // CRUD
    // =======================================================

    // Create Appointment
    const createAppointment = useCallback(async (appointmentData) => {
        const response = await axios.post(
            "/api/appointments",
            appointmentData
        );

        return response.data;
    }, []);

    // Update Appointment
    const updateAppointment = useCallback(
        async (appointmentId, appointmentData) => {
            const response = await axios.put(
                `/api/appointments/${appointmentId}`,
                appointmentData
            );

            return response.data;
        },
        []
    );

    // Delete Appointment
    const deleteAppointment = useCallback(async (appointmentId) => {
        const response = await axios.delete(
            `/api/appointments/${appointmentId}`
        );

        return response.data;
    }, []);

    // =======================================================
    // Reschedule Appointment
    // -------------------------------------------------------
    // Moves an appointment to another day/time.
    // Returns the backend response.
    // =======================================================

    const rescheduleAppointment = useCallback(
        async (appointmentId, dayId, time) => {
            const response = await axios.put(
                `/api/appointments/${appointmentId}/reschedule`,
                {
                    dayId,
                    time,
                }
            );

            return response.data;
        },
        []
    );

    // =======================================================
    // APPOINTMENT ACTIONS
    // =======================================================

    // Toggle Confirmation
    const toggleAppointmentConfirmation = useCallback(
        async (appointmentId) => {
            const response = await axios.put(
                `/api/appointments/${appointmentId}/toggle-confirmation`
            );

            return response.data;
        },
        []
    );

    return {
        appointments,

        // Search
        fetchAppointments,

        // Calendar
        fetchAvailableDaysAndBookingStatus,

        // CRUD
        createAppointment,
        updateAppointment,
        deleteAppointment,
        rescheduleAppointment,

        // Actions
        toggleAppointmentConfirmation,
    };
};

export default useAppointments;